import React from 'react';
import './OrderDetailsModal.css';

const OrderDetailsModal = ({ order, onClose, onStatusChange }) => {
  if (!order) return null;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    return `status-badge status-${status}`;
  };

  const getStatusText = (status) => {
    const statusMap = {
      'in_progress': 'In Progress',
      'prepared': 'Prepared',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const printOrder = () => {
    window.print();
  };

  const printThermalReceipt = () => {
    const printWindow = window.open('', '_blank');
    const receipt = generateThermalReceipt(order);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order #${order.orderNumber || order.id.slice(-4)}</title>
        <style>
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
          }

          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            margin: 0;
            padding: 8px;
            width: 80mm;
          }

          .center {
            text-align: center;
          }

          .bold {
            font-weight: bold;
          }

          .large {
            font-size: 14px;
          }

          .divider {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }

          .line {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
          }

          .item-row {
            margin: 4px 0;
          }

          .total-line {
            font-weight: bold;
            font-size: 13px;
            margin-top: 8px;
          }

          .footer {
            margin-top: 12px;
            text-align: center;
            font-size: 11px;
          }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        ${receipt}
      </body>
      </html>
    `);

    printWindow.document.close();
  };

  const generateThermalReceipt = (order) => {
    const orderTypeText = order.orderType === 'delivery' ? 'DELIVERY' : 'TAKEAWAY';
    const date = new Date();
    const dateStr = date.toLocaleDateString('en-IN');
    const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

    let html = `
      <div class="center bold large">ODISHA ZAIKA</div>
      <div class="center">Authentic Odia Cuisine</div>
      <div class="center">Ph: 7873611363</div>
      <div class="divider"></div>

      <div class="bold">Order #${order.orderNumber || order.id.slice(-4)}</div>
      <div>Date: ${dateStr}  Time: ${timeStr}</div>
      <div class="bold">${orderTypeText}</div>
      <div class="divider"></div>

      <div class="bold">Customer Details:</div>
      <div>Name: ${order.customer?.name || 'N/A'}</div>
      <div>Phone: ${order.customer?.phone || 'N/A'}</div>
    `;

    if (order.orderType === 'delivery') {
      if (order.customer?.building) {
        html += `<div>Building: ${order.customer.building}</div>`;
      }
      html += `<div>Location: ${order.customer?.location || 'N/A'}</div>`;
      html += `<div>Address: ${order.customer?.address || 'N/A'}</div>`;
    }

    html += `<div class="divider"></div>`;
    html += `<div class="bold">Order Items:</div>`;

    // Add items
    order.items?.forEach((item, idx) => {
      const vegLabel = item.isVeg ? '[V]' : '[NV]';
      html += `
        <div class="item-row">
          <div>${idx + 1}. ${vegLabel} ${item.name}</div>
          <div class="line">
            <span>   ${item.quantity} x ₹${item.price}</span>
            <span>₹${item.total || item.price * item.quantity}</span>
          </div>
        </div>
      `;
    });

    html += `<div class="divider"></div>`;

    // Pricing
    html += `
      <div class="line">
        <span>Subtotal:</span>
        <span>₹${order.pricing?.subtotal || 0}</span>
      </div>
    `;

    if (order.pricing?.tax > 0) {
      html += `
        <div class="line">
          <span>Tax:</span>
          <span>₹${order.pricing.tax}</span>
        </div>
      `;
    }

    if (order.pricing?.deliveryCharge > 0) {
      html += `
        <div class="line">
          <span>Delivery Charge:</span>
          <span>₹${order.pricing.deliveryCharge}</span>
        </div>
      `;
    }

    html += `<div class="divider"></div>`;
    html += `
      <div class="line total-line">
        <span>TOTAL:</span>
        <span>₹${order.pricing?.total || 0}</span>
      </div>
    `;

    html += `<div class="divider"></div>`;

    // Payment info
    const paymentText = order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Paid';
    html += `<div class="bold">Payment: ${paymentText}</div>`;

    // Special instructions
    if (order.specialInstructions) {
      html += `<div class="divider"></div>`;
      html += `<div class="bold">Special Instructions:</div>`;
      html += `<div>${order.specialInstructions}</div>`;
    }

    // Footer
    html += `
      <div class="divider"></div>
      <div class="footer">
        <div>Thank you for ordering!</div>
        <div>Visit again!</div>
      </div>
    `;

    return html;
  };

  const callCustomer = () => {
    if (order.customer?.phone) {
      window.location.href = `tel:${order.customer.phone}`;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content order-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Order Details</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Order Info */}
          <div className="details-section">
            <div className="section-header">
              <h3>Order Information</h3>
              <span className={getStatusBadgeClass(order.status)}>
                {getStatusText(order.status)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Order Number:</span>
              <span className="detail-value">#{order.orderNumber || order.id.slice(-4)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Order Placed:</span>
              <span className="detail-value">{formatTimestamp(order.timestamp)}</span>
            </div>
            {order.orderType && (
              <div className="detail-row">
                <span className="detail-label">Order Type:</span>
                <span className="detail-value">
                  {order.orderType === 'delivery' ? '🚚 Delivery' : '🏃 Takeaway'}
                </span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Payment Method:</span>
              <span className="detail-value">
                {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Paid'}
              </span>
            </div>
          </div>

          {/* Customer Info */}
          <div className="details-section">
            <h3>Customer Information</h3>
            <div className="detail-row">
              <span className="detail-label">👤 Name:</span>
              <span className="detail-value">{order.customer?.name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">📱 Phone:</span>
              <span className="detail-value">
                {order.customer?.phone || 'N/A'}
                {order.customer?.phone && (
                  <button className="btn-call" onClick={callCustomer}>
                    Call
                  </button>
                )}
              </span>
            </div>
            {order.orderType === 'delivery' && (
              <>
                {order.customer?.building && (
                  <div className="detail-row">
                    <span className="detail-label">🏢 Building/Landmark:</span>
                    <span className="detail-value">{order.customer.building}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">📍 Location:</span>
                  <span className="detail-value">
                    {order.customer?.location || 'N/A'}
                    {order.customer?.latitude && order.customer?.longitude && (
                      <>
                        <br />
                        <small style={{ fontSize: '11px', color: '#666' }}>
                          {order.customer.latitude.toFixed(6)}, {order.customer.longitude.toFixed(6)}
                        </small>
                        <br />
                        <a
                          href={`https://www.google.com/maps?q=${order.customer.latitude},${order.customer.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-map"
                          style={{
                            marginTop: '4px',
                            display: 'inline-block',
                            padding: '4px 8px',
                            background: '#4285F4',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          🗺️ Open in Google Maps
                        </a>
                      </>
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">🏠 Address:</span>
                  <span className="detail-value">{order.customer?.address || 'N/A'}</span>
                </div>
              </>
            )}
          </div>

          {/* Order Items */}
          <div className="details-section">
            <h3>Order Items</h3>
            <div className="items-table">
              {order.items?.map((item, idx) => (
                <div key={idx} className="item-detail-row">
                  <span className="veg-icon" style={{
                    borderColor: item.isVeg ? '#4CAF50' : '#f44336'
                  }}>
                    <span className="veg-dot" style={{
                      backgroundColor: item.isVeg ? '#4CAF50' : '#f44336'
                    }}></span>
                  </span>
                  <span className="item-name">{item.name}</span>
                  <span className="item-quantity">× {item.quantity}</span>
                  <span className="item-price">₹{item.price}</span>
                  <span className="item-total">₹{item.total || item.price * item.quantity}</span>
                </div>
              ))}
            </div>

            <div className="pricing-summary">
              <div className="price-row">
                <span>Subtotal:</span>
                <span>₹{order.pricing?.subtotal || 0}</span>
              </div>
              {order.pricing?.tax > 0 && (
                <div className="price-row">
                  <span>Tax:</span>
                  <span>₹{order.pricing.tax}</span>
                </div>
              )}
              {order.pricing?.deliveryCharge > 0 && (
                <div className="price-row">
                  <span>Delivery Charge:</span>
                  <span>₹{order.pricing.deliveryCharge}</span>
                </div>
              )}
              <div className="price-row total-row">
                <span>Total:</span>
                <span>₹{order.pricing?.total || 0}</span>
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          {order.specialInstructions && (
            <div className="details-section">
              <h3>Special Instructions</h3>
              <div className="special-notes">
                {order.specialInstructions}
              </div>
            </div>
          )}

          {/* Order Timeline */}
          {order.timeline && (
            <div className="details-section">
              <h3>Order Timeline</h3>
              <div className="timeline">
                {order.timeline.orderPlaced && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Order Placed</div>
                      <div className="timeline-time">{formatTimestamp(order.timeline.orderPlaced)}</div>
                    </div>
                  </div>
                )}
                {order.timeline.confirmed && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Confirmed</div>
                      <div className="timeline-time">{formatTimestamp(order.timeline.confirmed)}</div>
                    </div>
                  </div>
                )}
                {order.timeline.prepared && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Prepared</div>
                      <div className="timeline-time">{formatTimestamp(order.timeline.prepared)}</div>
                    </div>
                  </div>
                )}
                {order.timeline.out_for_delivery && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Out for Delivery</div>
                      <div className="timeline-time">{formatTimestamp(order.timeline.out_for_delivery)}</div>
                    </div>
                  </div>
                )}
                {order.timeline.delivered && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Delivered</div>
                      <div className="timeline-time">{formatTimestamp(order.timeline.delivered)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="modal-actions">
            <button className="btn-thermal-print" onClick={printThermalReceipt}>
              🧾 Thermal Print
            </button>
            <button className="btn-print" onClick={printOrder}>
              🖨️ Regular Print
            </button>
            <button className="btn-close-modal" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
